import { useEffect, useMemo } from 'react';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useRefreshEhrData from '../../PatientDetail/Header/EhrRefreshButton/useRefreshEhrData';
import { patientPropsSelector } from '../../../../redux/selectors/patientSelector';
import { wasMemberIngested } from './utils';
import {
  getElationPatientChartUrl,
  getElationPatientId,
} from '../../../../util/elation';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';

const fhirService = new FhirService('Patient');

const dateTimeParser = new FhirDateTime();

const formatBirthDate = (birthDate?: string): string => {
  if (!birthDate) {
    return '';
  }
  const formatted = dateTimeParser.getDisplayValue(birthDate);
  return formatted ?? birthDate;
};

type UseMemberDetailDataParams = {
  accountId?: string;
  memberId?: string;
  isMasked: boolean;
  mrnConfig?: unknown;
};

export const useMemberDetailData = ({
  accountId,
  memberId,
  isMasked,
  mrnConfig,
}: UseMemberDetailDataParams) => {
  const [fetchMember, fetchMemberState] = useService(fhirService.getById);

  useEffect(() => {
    if (accountId && memberId) {
      fetchMember(accountId, memberId);
    }
  }, [accountId, memberId, fetchMember]);

  const member = fetchMemberState.response?.data || null;
  const memberProps = member
    ? patientPropsSelector(member, isMasked, mrnConfig)
    : null;
  const memberDisplayName = useMemo(() => {
    const memberFirstName = memberProps?.name?.given || '';
    const memberLastName = memberProps?.name?.family || '';
    const memberName = [memberFirstName, memberLastName]
      .filter(Boolean)
      .join(' ');
    return memberName || memberProps?.name?.display || '';
  }, [memberProps]);
  const memberBirthDate = memberProps?.birthDate || member?.birthDate || '';
  const formattedBirthDate = formatBirthDate(memberBirthDate);
  const elationChartUrl = getElationPatientChartUrl(
    getElationPatientId(member),
  );
  const memberLoadError = fetchMemberState.error;
  const memberNotFound = memberLoadError?.response?.status === 404;
  const hasLoadedMember = Boolean(fetchMemberState.lastFetchAttempt);
  const isLoading = fetchMemberState.isFetching && !member;
  const canSync = !!member && wasMemberIngested(member);
  const refreshEhrData = useRefreshEhrData(member as fhir.Patient);

  return {
    member,
    memberProps,
    memberDisplayName,
    formattedBirthDate,
    elationChartUrl,
    memberLoadError,
    memberNotFound,
    hasLoadedMember,
    isLoading,
    canSync,
    refreshEhrData,
  };
};
